import { z } from 'zod';
import { AssignmentInput, Invite, InviteInput, MoneyLevelSchema } from './schemas';

/**
 * Орг-структура и справочник сотрудников (раздел 6.1 ТЗ, поток 1).
 * Справочник — это люди компании, которых ещё может не быть в портале: почта необязательна.
 * Доступ в портал человек получает только после приглашения и по явному решению администратора.
 */
const Uuid = z.uuid();

export const OrgUnit = z.object({
  id: Uuid,
  parentId: Uuid.nullable(),
  code: z.string().nullable(),
  name: z.string(),
  headStaffId: Uuid.nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  staffCount: z.number().int(),
});
export type OrgUnit = z.infer<typeof OrgUnit>;

export const OrgUnitInput = z.object({
  parentId: Uuid.nullable().optional(),
  code: z.string().trim().max(40).nullable().optional(),
  name: z.string().trim().min(1, 'Укажите название').max(200),
  headStaffId: Uuid.nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(100000).optional(),
});
export type OrgUnitInput = z.infer<typeof OrgUnitInput>;

export const StaffStatusSchema = z.enum(['active', 'leave', 'dismissed']);
export type StaffStatus = z.infer<typeof StaffStatusSchema>;

export const StaffInput = z.object({
  fullName: z.string().trim().min(2, 'Укажите ФИО').max(200),
  position: z.string().trim().max(300).nullable().optional(),
  orgUnitId: Uuid.nullable().optional(),
  email: z.string().trim().toLowerCase().email('Неверная почта').max(200).nullable().optional(),
  phone: z.string().trim().max(60).nullable().optional(),
  hiredOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  fte: z.number().min(0.05).max(5).optional(),
  status: StaffStatusSchema.optional(),
  /** Метка группы, например «КУС» — для выборки при массовых действиях */
  groupLabel: z.string().trim().max(60).nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
});
export type StaffInput = z.infer<typeof StaffInput>;

export const StaffMember = z.object({
  id: Uuid,
  fullName: z.string(),
  position: z.string().nullable(),
  orgUnitId: Uuid.nullable(),
  orgUnitName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  hiredOn: z.string().nullable(),
  fte: z.number(),
  status: StaffStatusSchema,
  groupLabel: z.string().nullable(),
  note: z.string().nullable(),
  /** Есть вход в портал */
  membershipId: Uuid.nullable(),
  /** Приглашение отправлено и ещё не принято */
  invitePending: z.boolean(),
  roleNames: z.array(z.string()),
});
export type StaffMember = z.infer<typeof StaffMember>;

/** Строка импорта: одна и та же схема для проверки и записи */
export const StaffImportRow = StaffInput.extend({
  /** Название подразделения; если такого нет — создаётся в корне */
  orgUnitName: z.string().trim().max(200).nullable().optional(),
});
export type StaffImportRow = z.infer<typeof StaffImportRow>;

export const StaffImportInput = z.object({
  rows: z.array(StaffImportRow).min(1).max(1000),
  /** true — только показать, что изменится */
  dryRun: z.boolean().default(true),
});
export type StaffImportInput = z.infer<typeof StaffImportInput>;

export const StaffImportResult = z.object({
  dryRun: z.boolean(),
  created: z.number().int(),
  updated: z.number().int(),
  unitsCreated: z.number().int(),
  problems: z.array(z.object({ row: z.number().int(), message: z.string() })),
});
export type StaffImportResult = z.infer<typeof StaffImportResult>;

/** Приглашение из карточки сотрудника: почта берётся из справочника или указывается здесь */
export const StaffInviteInput = z.object({
  email: z.string().trim().toLowerCase().email('Неверная почта').max(200).optional(),
  moneyLevel: MoneyLevelSchema.default('none'),
  assignments: z.array(AssignmentInput).min(1, 'Назначьте хотя бы одну роль').max(100),
});
export type StaffInviteInput = z.infer<typeof StaffInviteInput>;

export const StaffInviteResult = Invite;
export { InviteInput };
